import React from 'react'
import { useOutletContext } from 'react-router-dom'
import { Card, Typography, Slider, InputNumber, Flex, Alert } from 'antd'
import type { TestEditorContext } from '../../index'

const { Title, Text } = Typography

const COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1']

export const TrafficSplitPage: React.FC = () => {
  const { baseFlow, leadFlows, setLeadFlows } =
    useOutletContext<TestEditorContext>()

  if (leadFlows.length === 0 && !baseFlow) {
    return (
      <Alert
        title="No Variants Created"
        description="Please go back and select at least two lead flows to configure traffic split."
        type="info"
        showIcon
      />
    )
  }

  const totalTraffic = leadFlows.reduce(
    (sum, f) => sum + f.traffic_percentage,
    0,
  )
  const remaining = 100 - totalTraffic

  const getMaxForFlow = (flowId: string) => {
    const othersTotal = leadFlows
      .filter((f) => f.flow_id !== flowId)
      .reduce((sum, f) => sum + f.traffic_percentage, 0)
    return 100 - othersTotal
  }

  const handleTrafficChange = (flowId: string, newValue: number) => {
    const max = getMaxForFlow(flowId)
    setLeadFlows(
      leadFlows.map((f) =>
        f.flow_id === flowId
          ? {
              ...f,
              traffic_percentage: Math.max(0, Math.min(max, newValue)),
            }
          : f,
      ),
    )
  }

  return (
    <div>
      <Flex vertical gap="large" style={{ width: '100%' }}>
        <div>
          <Title level={4}>Configure Traffic Split</Title>
          <Text type="secondary">
            Allocate traffic percentages for each variant. Remaining traffic
            goes to the default flow.
          </Text>
        </div>

        <Card
          title="Traffic Allocation"
          style={{ maxWidth: 700 }}
          extra={
            <Text
              strong
              style={{ color: remaining >= 0 ? '#52c41a' : '#f5222d' }}
            >
              Remaining: {remaining}%
            </Text>
          }
        >
          <Flex vertical gap="large" style={{ width: '100%' }}>
            {baseFlow && (
              <div>
                <Flex justify="space-between" align="center">
                  <Text strong style={{ minWidth: 100 }}>
                    {baseFlow.name} (Control)
                  </Text>
                  <Text type="secondary">
                    Remaining: {remaining < 0 ? 0 : remaining}%
                  </Text>
                </Flex>
              </div>
            )}
            {leadFlows.map((flow, index) => (
              <div key={flow.flow_id}>
                <Flex justify="space-between" align="center">
                  <Text strong style={{ minWidth: 100 }}>
                    {flow.flow_name}
                  </Text>
                  <Flex gap="small" align="center">
                    <Slider
                      min={0}
                      max={100}
                      value={flow.traffic_percentage}
                      onChange={(value) =>
                        handleTrafficChange(flow.flow_id, value)
                      }
                      style={{ width: 200 }}
                      trackStyle={{
                        backgroundColor: COLORS[index % COLORS.length],
                      }}
                    />
                    <InputNumber
                      min={0}
                      max={100}
                      value={flow.traffic_percentage}
                      onChange={(value) =>
                        handleTrafficChange(flow.flow_id, value || 0)
                      }
                      formatter={(value) => `${value}%`}
                      parser={(value) =>
                        parseInt(value?.replace('%', '') || '0', 10)
                      }
                      style={{ width: 80 }}
                    />
                  </Flex>
                </Flex>
              </div>
            ))}
          </Flex>
        </Card>
      </Flex>
    </div>
  )
}
